import { useNavigate } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { OfficeAddress } from '../components/OfficeAddress';
import { ShieldCheck, Award, Users, Clock, Zap, MapPin, ArrowRight } from "lucide-react";

const VALUES = [
  { icon: Award, title: "Licensing Information", desc: "Listed license holder: Larry Honor. Electrical License ES 12002198. Verify current classification, status, and scope through Florida DBPR." },
  { icon: ShieldCheck, title: "Safety First", desc: "Work is planned around applicable code requirements and inspection coordination. Inspection outcomes are determined by the authority having jurisdiction." },
  { icon: Users, title: "People Over Jobs", desc: "Clear communication and honest estimates — we treat your home like our own." },
  { icon: Clock, title: "Scheduling Coordination", desc: "Scheduling and access are agreed with you before work begins. The office communicates project changes." },
];

export default function About() {
  const navigate = useNavigate();
  return (
    <PublicShell>
      <div data-testid="about-page">
        <div className="mb-3 text-xs font-bold tracking-[0.25em] text-[#1286F2]">ABOUT HARDWIRE ELECTRICAL</div>
        <h1 className="font-display text-4xl font-black uppercase leading-tight tracking-tight text-slate-900 sm:text-5xl">
          Powering today.<br /><span className="text-[#1286F2]">Building tomorrow.</span>
        </h1>
        <div className="mt-6 text-sm leading-relaxed text-slate-600">
          <p>Hardwire Electrical LLC is based in Ocala, Florida. We work with homeowners, business customers, and builders to define electrical project scope and coordinate the next steps. Contact the office to confirm service availability for your address.</p>
        </div>
        <section className="mt-10 border-l-4 border-[#1286F2] bg-slate-50 p-7" data-testid="larry-profile">
          <p className="text-xs font-bold uppercase tracking-widest text-[#1286F2]">The person behind Hardwire</p>
          <h2 className="mt-3 text-base font-bold md:text-lg">Larry Honor</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">Hardwire's listed license holder. For a significant residential, commercial, or builder project, start with a conversation about scope, project requirements, and the documentation you need before making a commitment.</p>
          <button onClick={() => navigate('/contact?topic=Speak%20with%20Larry')} className="mt-5 font-bold text-[#1286F2]" data-testid="larry-contact">Ask to speak with Larry →</button>
        </section>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {VALUES.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-[#1286F2]"><Icon size={24} /></span>
                <h3 className="mt-4 font-display text-xl font-extrabold text-slate-900">{v.title}</h3>
                <p className="mt-1 text-slate-500">{v.desc}</p>
              </div>
            );
          })}
        </div>

        <div id="licensing" className="mt-10 scroll-mt-28 rounded-2xl border border-slate-200 p-6" data-testid="about-licensing">
          <h2 className="font-display text-2xl font-extrabold text-slate-900">Licensing & Office</h2>
          <div className="mt-4"><OfficeAddress testId="about-office-address" /></div>
          <p className="mt-4 text-sm text-slate-500" data-testid="about-license-note">Existing business licensing details are listed below. Contact the office for current license and insurance documentation and scope-specific verification.</p>
          <ul className="mt-4 space-y-2 text-slate-600">
            <li className="flex items-center gap-2"><Zap size={16} className="text-[#1286F2]" /> License Holder: <strong className="text-slate-800">Larry Honor</strong></li>
            <li className="flex items-center gap-2"><Zap size={16} className="text-[#1286F2]" /> Electrical License: <strong className="text-slate-800">ES 12002198</strong></li>
            <li className="flex items-center gap-2"><Zap size={16} className="text-[#1286F2]" /> County Certification: <strong className="text-slate-800">24483</strong></li>
            <li className="flex items-start gap-2"><MapPin size={16} className="mt-1 shrink-0 text-[#1286F2]" /> Ocala office · Contact us to confirm coverage for your project address.</li>
          </ul>
          <div className="mt-5 flex flex-wrap gap-5 text-sm font-bold">
            <a href="https://www.myfloridalicense.com/wl11.asp" target="_blank" rel="noreferrer" className="text-[#1286F2]" data-testid="license-verification-link">Verify classification & status with Florida DBPR ↗</a>
            <button onClick={() => navigate('/contact?topic=License%20and%20insurance%20documentation')} className="text-[#1286F2]" data-testid="insurance-documentation-link">Request insurance documentation →</button>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center gap-4 rounded-2xl bg-[#0B1120] p-8 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-white">Ready to start your project?</h2>
            <p className="mt-1 text-slate-300">Get a fast, no-pressure quote today.</p>
          </div>
          <button onClick={() => navigate("/quote")} className="flex items-center gap-2 rounded-xl bg-[#1286F2] px-7 py-3.5 font-bold text-white hover:bg-[#0A84FF]" data-testid="about-quote-btn">
            Get a Free Quote <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </PublicShell>
  );
}
